// external imports
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import Pako from 'pako';
import { Buffer } from 'buffer';

// local imports
import {
	smelterFieldsUploadAutoQC,
	smelterFieldsUploadManualQC
} from '../../tables/tableFields';
import CapturesTable from '../../tables/CapturesTable';
import exportConfig from '../../../config';
import config from '../../../config';
import PostQCDataTable from '../../tables/PostQCDataTable';

// custom hooks & context
import { pipelineContext } from '../../../contexts/pipelineContext';
import useGainsQCApi from '../../../services/useGainsQCApi';
import { endpointContext } from '../../../contexts/endpointContext';

// upload helpers
import { getPageNumbers, copyPostQCLogToClipboard } from './uploadHelpers';
import { toastContentDiv, renderToastTable, toggleToast } from './toastHelpers';
import { renderPagination, renderToggleFill } from './paginationHelpers';
import {
	renderPostQCButton,
	renderFinishQCButton,
	renderUploadButton
} from './buttons';
import { ProcessTimerToast } from './toastHelpers';

// const statuses = {
// 	0: 'NOT STARTED',
// 	1: 'LOADING',
// 	2: 'COMPLETED',
// 	3: 'ERROR'
// };

const QCUpload = ({
	props,
	formValue,
	qcTimings,
	setQCTimings,
	setFormValue
}) => {
	// captures
	const [manCapsUpload, setManCapsUpload] = useState([]);
	const [capsToSendBack, setCapsToSendBack] = useState([]);

	// toggles / user options
	const [manCapsCollapsed, setManCapsCollapsed] = useState(true);
	const [autoCapsCollapsed, setAutoCapsCollapsed] = useState(false);
	const [isToastVisible, setIsToastVisible] = useState(false);
	const [toggleFill, setToggleFill] = useState(true);
	const [overwriteOnPostQC, setOverwriteOnPostQC] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	// tables data
	const [postQCData, setPostQCData] = useState();
	const [postQCDataFiltered, setPostQCDataFiltered] = useState(); // filtering captures in postQCData between capture date
	const [tablesData, setTablesData] = useState(null);
	const [totalPages, setTotalPages] = useState(null);
	const itemsPerPage = 7;
	const [paginatedData, setPaginatedData] = useState(null);
	const [uniqueSites, setUniqueSites] = useState(0);

	// statuses
	const [postQCResponse, setPostQCResponse] = useState([]);
	const [uploadResponse, setUploadResponse] = useState([]);

	const indexCalcStatusRef = useRef(0);
	const websocketStatusRef = useRef(0);
	const fetchWSDataStatusRef = useRef(0);
	const postQCStatusRef = useRef(0);
	const uploadStatusRef = useRef(0);

	const [error, setError] = useState(false);

	const [postQCAttempts, setPostQCAttempts] = useState(0);
	const [uploadAttempts, setUploadAttempts] = useState(0);

	const noPostQCData =
		indexCalcStatusRef.current > 1 &&
		postQCStatusRef.current > 1 &&
		postQCData &&
		postQCData.length === 0;

	const userHandle = `${props.authProps.userAttributes.givenName.toLowerCase()}.${props.authProps.userAttributes.familyName.toLowerCase()}`;

	const [toastContent, setToastContent] = useState([]);
	const toastIdRef = useRef(null);
	const [executionID, setExecutionID] = useState(null);
	const [socketUrl, setSocketUrl] = useState(null);

	const { makeRequest: sendCapsBack } = useGainsQCApi();
	const runIndexCalc = useGainsQCApi();
	const uploadToDB = useGainsQCApi();

	const pipelineContextQCUpload = useContext(pipelineContext);
	const endpointContextQCUpload = useContext(endpointContext);

	const reconnectAttempts = useRef(0);
	const ws = useRef(null);
	const isUnmounted = useRef(false);

	// timers
	const [postQCProcessState, setPostQCProcessState] = useState('idle'); // idle | running | complete | error
	const [postQCElapsedSeconds, setPostQCElapsedSeconds] = useState(0);
	const postQCProcessStateRef = useRef('idle');
	const postQCStartedAtRef = useRef(null);
	const postQCFinishedAtRef = useRef(null);
	const [uploadProcessState, setUploadProcessState] = useState('idle'); // idle | running | complete | error
	const uploadProcessStateRef = useRef('idle');
	const uploadStartedAtRef = useRef(null);
	const uploadFinishedAtRef = useRef(null);

	const setPostQCProcessStatus = (status) => {
		postQCProcessStateRef.current = status;
		setPostQCProcessState(status);
	};

	const setUploadProcessStatus = (status) => {
		uploadProcessStateRef.current = status;
		setUploadProcessState(status);
	};

	const postQCDataLoading =
		runIndexCalc.loading ||
		indexCalcStatusRef.current === 1 ||
		websocketStatusRef.current === 1 ||
		fetchWSDataStatusRef.current === 1;

	const addToastContent = (content, error = false) => {
		setToastContent((prevItems) => [
			...prevItems,
			[
				new Date().toLocaleTimeString('en-GB'),
				<>
					{error && <i className="fa-solid fa-triangle-exclamation pe-2" />}
					{content}
				</>
			]
		]);
	};

	useEffect(() => {
		if (postQCProcessState !== 'running') return;

		const intervalId = window.setInterval(() => {
			const elapsedMilliseconds = Date.now() - postQCStartedAtRef.current;

			setPostQCElapsedSeconds(Math.floor(elapsedMilliseconds / 1000));
		}, 1000);

		return () => window.clearInterval(intervalId);
	}, [postQCProcessState]);

	const startPostQCProcess = () => {
		postQCStartedAtRef.current = Date.now();
		postQCFinishedAtRef.current = null;
		setPostQCProcessStatus('running');
	};

	const completePostQCProcess = () => {
		postQCFinishedAtRef.current = Date.now();
		setPostQCProcessStatus('complete');
	};

	console.log('postQCPrcessing', postQCProcessState, postQCProcessStateRef);

	const failPostQCProcess = () => {
		postQCFinishedAtRef.current = Date.now();
		setPostQCProcessStatus('error');
	};

	const startUploadProcess = () => {
		uploadStartedAtRef.current = Date.now();
		uploadFinishedAtRef.current = null;
		setUploadProcessStatus('running');
	};

	const completeUploadProcess = () => {
		uploadFinishedAtRef.current = Date.now();
		setUploadProcessStatus('complete');
	};

	const failUploadProcess = () => {
		uploadFinishedAtRef.current = Date.now();
		setUploadProcessStatus('error');
	};

	useEffect(() => {
		const statuses = [
			indexCalcStatusRef.current,
			websocketStatusRef.current,
			fetchWSDataStatusRef.current
		];

		if (statuses.every((status) => status === 1)) {
			postQCStatusRef.current = 1;
		} else if (statuses.some((status) => status === 3)) {
			postQCStatusRef.current = 3;
		}
		// else if (statuses.some((status) => status === 3)) {
		// 	postQCStatusRef.current = 3;
		// }
	}, [
		indexCalcStatusRef.current,
		websocketStatusRef.current,
		fetchWSDataStatusRef.current
	]);

	useEffect(() => {
		const style = {
			minWidth: '600px',
			textAlign: 'left',
			background: '#333',
			color: '#fff'
		};

		const toastProps = {
			copyPostQCLogToClipboard,
			toggleToast,
			indexCalcStatusRef,
			fetchWSDataStatusRef,
			uploadStatusRef,
			websocketStatusRef,
			setIsToastVisible
		};

		if (isToastVisible) {
			if (!toastIdRef.current) {
				toastIdRef.current = toast(toastContentDiv(toastContent, toastProps), {
					duration: 300000,
					style: style
				});
			} else {
				toast(toastContentDiv(toastContent, toastProps), {
					id: toastIdRef.current,
					duration: 300000,
					style: style
				});
			}
		} else {
			if (toastIdRef.current) {
				toast.dismiss(toastIdRef.current);
				toastIdRef.current = null;
			}
		}
	}, [isToastVisible, toastContent]);

	useEffect(
		() => {
			/**
			 * determine the caps to upload
			 * and caps to remove
			 */

			pipelineContextQCUpload.setQCInProgress(false);

			if (!formValue.uploadStaticPostQCDataFile) {
				setManCapsUpload(
					pipelineContextQCUpload.comm === 'stockpiles'
						? pipelineContextQCUpload.capsInPipeline.filter(
								(cap) => cap['qc_state'] === 4
							)
						: pipelineContextQCUpload.capsInPipeline.filter((cap) =>
								cap.hasOwnProperty('newQC')
							)
				);

				setCapsToSendBack(
					pipelineContextQCUpload.comm === 'stockpiles'
						? pipelineContextQCUpload.capsInPipeline.filter(
								(cap) => cap['qc_state'] !== 4
							)
						: pipelineContextQCUpload.capsInPipeline.filter(
								(cap) => cap.state === null
							)
				);

				const uniqueSiteIds = [
					...new Set(
						pipelineContextQCUpload.capsInPipeline
							.filter((cap) => cap.state !== null)
							.map((item) => item.site_id)
					)
				];
				setUniqueSites(uniqueSiteIds.length);

				setQCTimings({
					...qcTimings,
					qcEnd: new Date()
				});
			}

			// needs to depend on progressBar to retrigger this calc when moving to upload
		},
		[
			//pipelineContextQCUpload.capsInPipeline,
			//pipelineContextQCUpload.progressBar
		]
	);

	const runPostQCIndexCalc = async () => {
		/**
		 * perform index calculation
		 */

		startPostQCProcess();

		if (indexCalcStatusRef.current === 2 || postQCAttempts >= 2) return;
		if (!isToastVisible) toggleToast(setIsToastVisible);

		setPostQCAttempts((prev) => prev + 1);
		indexCalcStatusRef.current = 1;

		addToastContent(
			`QC upload starting: ${pipelineContextQCUpload.qcType} / ${userHandle}`
		);

		addToastContent('Index calc: starting ...');
		// addToastContent(
		// 	<ProcessTimerToast
		// 		startedAtRef={postQCStartedAtRef}
		// 		finishedAtRef={postQCFinishedAtRef}
		// 		processStateRef={postQCProcessStateRef}
		// 		runningMessage="Processing post-QC data"
		// 		completedMessage="Post-QC data processing completed"
		// 		errorMessage="Processing post-QC data failed"
		// 	/>
		// );

		// TODO: add more details about QC upload type
		// ie commodity, start/end date, or specific site IDs
		// should also log this to slack/db for traceability

		var siteIds = [];
		var indexCalcBody;

		if (!pipelineContextQCUpload.straightToUpload) {
			if (pipelineContextQCUpload.qcType === 'Daily') {
				indexCalcBody = {
					prod_states: formValue.formProdStates.join(),
					commodity_ids: formValue.formCommodities.join(),
					start_date: formValue.formStartDate,
					end_date: formValue.formEndDate,
					overwrite: overwriteOnPostQC
				};
			}

			if (pipelineContextQCUpload.qcType === 'Historic') {
				indexCalcBody = {
					site_ids: formValue.formSiteId,
					commodity_ids: formValue.formCommodities.join(),
					start_date: formValue.formStartDate,
					//end_date: formValue.formEndDate,
					overwrite: overwriteOnPostQC
				};
			}

			if (pipelineContextQCUpload.qcType === 'Re-QC') {
				indexCalcBody = {
					site_ids: formValue.formSiteId,
					commodity_ids: formValue.formCommodities.join(),

					// take first Re-QC'd cap captured date as start date
					start_date: pipelineContextQCUpload.capsInPipeline[0].captured,
					overwrite: overwriteOnPostQC
				};
			}

			if (pipelineContextQCUpload.qcType === 'straightToQCFromUserCaps') {
				const uniqueSiteIds2 = [
					...new Set(
						pipelineContextQCUpload.capsInPipeline
							.filter((cap) => cap.state !== null)
							.map((item) => item.site_id)
					)
				];

				indexCalcBody = {
					site_ids: uniqueSiteIds2,
					//commodity_ids: formValue.formCommodities.join(),
					start_date: pipelineContextQCUpload.capsInPipeline[0].captured,
					overwrite: overwriteOnPostQC
				};
			}
		} else {
			if (formValue.manuallySelectSites) {
				siteIds = formValue.formSiteId;

				indexCalcBody = {
					site_ids: siteIds,
					// prod_states: formValue.prod_states,
					// commodities: formValue.commodities,
					start_date: formValue.formStartDate,
					end_date: formValue.formEndDate,
					overwrite: overwriteOnPostQC
				};
			} else {
				indexCalcBody = {
					//site_ids: siteIds,
					prod_states: formValue.formProdStates.join(),
					commodity_ids: formValue.formCommodities.join(),
					start_date: formValue.formStartDate,
					//end_date: formValue.formEndDate,
					overwrite: overwriteOnPostQC
				};
			}
		}

		addToastContent(
			`Index calculation body: ${JSON.stringify(indexCalcBody, null, 2)}`
		);

		await runIndexCalc.makeRequest(
			`/smelters/init-post-qc`,
			'POST',
			indexCalcBody,
			pipelineContextQCUpload.notificationsEnabled ? 'post QC' : null
		);
	};

	useEffect(() => {
		/**
		 * upon successful index calc, connect to websocket
		 */

		if (indexCalcStatusRef.current === 2) return;

		if (runIndexCalc.data && !runIndexCalc.error) {
			addToastContent('Index calc: successfully posted');
			addToastContent(`Execution ID: ${runIndexCalc?.data?.execution_id}`);

			indexCalcStatusRef.current = 2;
			setSocketUrl(runIndexCalc.data.response_url);
		} else if (runIndexCalc.error) {
			failPostQCProcess();

			indexCalcStatusRef.current = 3;
			postQCStatusRef.current = 3;

			setError({ stage: 'Index calculation', error: runIndexCalc?.error });
			addToastContent(<>Index calc: {error}</>, true);
			addToastContent(
				<>Index calc:{JSON.stringify(runIndexCalc.error)}</>,
				true
			);

			if (postQCResponse.length >= 2) {
				addToastContent(
					'Index calc: failed twice. Please exit QC and report any errors to an admin.',
					true
				);
			}

			setPostQCResponse((prevList) => [
				...prevList,
				{
					user: `${userHandle}`,
					time: new Date().toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
						second: '2-digit'
					}),
					date: new Date().toLocaleDateString('en-GB'),
					error: runIndexCalc.error,
					failed_at: 'init-post-qc',
					execution_id: null
				}
			]);
		} else {
			//
		}
	}, [runIndexCalc.data, runIndexCalc.error]);

	useEffect(() => {
		if (!socketUrl || postQCStatusRef.current === 2) return;

		const params = new URLSearchParams({
			execution_id: runIndexCalc.data.execution_id
		});

		// don't need executionID as a state
		setExecutionID(runIndexCalc.data.execution_id);

		const maxReconnectAttempts = 5;
		const reconnectDelay = 2000;
		let reconnectAttempts2 = 0;

		const connectWebSocket = () => {
			if (isUnmounted.current || postQCStatusRef.current === 2) return;

			//console.log('Attempting to connect WebSocket...');
			ws.current = new WebSocket(`${socketUrl}?${params.toString()}`);

			ws.current.onopen = () => {
				//console.log('WebSocket connection opened');
				websocketStatusRef.current = 1;

				addToastContent(
					<>
						<i className="fa-solid fa-plug-circle-check pe-2"></i> WS opened
					</>
				);

				reconnectAttempts.current = 0;
			};

			ws.current.onmessage = (event) => {
				//console.log('ws on message');
				let data = JSON.parse(JSON.parse(event.data));
				//console.log(data);

				addToastContent(
					<>
						<i class="fa-solid fa-plug pe-2"></i> WS {data.message}
					</>
				);

				if (
					data.message === 'Process status is SUCCEEDED' &&
					data.hasOwnProperty('url') &&
					data.url !== null
				) {
					//console.log('Process succeeded, fetching additional data...');

					addToastContent(`Index calc: completed`);
					addToastContent(`Post QC data: fetching ...`);

					fetch(data.url)
						.then((response) => {
							if (!response.ok) {
								//console.log('if response not ok');
								//console.log(response);
								failPostQCProcess();

								addToastContent('Post QC data: error', true);

								setError({
									stage:
										'websocket (fetching data from index calc url - response not OK)',
									error: response
								});

								setPostQCResponse((preList) => [
									...preList,
									{
										user: `${userHandle}`,
										time: new Date().toLocaleTimeString([], {
											hour: '2-digit',
											minute: '2-digit',
											second: '2-digit'
										}),
										date: new Date().toLocaleDateString('en-GB'),
										error: 'error fetching data from index calc',
										failed_at: 'websocket error',
										execution_id: runIndexCalc.data.execution_id
									}
								]);
								fetchWSDataStatusRef.current = 3;
								reconnectAttempts.current = 5;
								ws.current?.close();

								throw new Error('Failed to fetch secondary data');
							}
							return response.json();
						})
						.then((secondaryData) => {
							console.log('Secondary Data:', secondaryData);

							addToastContent(`Post QC data: successfully fetched`);

							setPostQCData(secondaryData);

							indexCalcStatusRef.current = 2;
							websocketStatusRef.current = 2;
							fetchWSDataStatusRef.current = 2;
							postQCStatusRef.current = 2;
							//reconnectAttempts.current = 5;
							ws.current?.close();
						})
						.catch((error) => {
							// front end behaviour of this needs work
							failPostQCProcess();

							console.error('Error fetching secondary data:', error);
							setError({
								stage: 'websocket (fetching secondary data - catch error)',
								error: error
							});

							addToastContent(`Post QC data: error`);
							addToastContent(
								`Post QC data: ${
									typeof error === 'object' ? JSON.stringify(error) : error
								}`
							);

							fetchWSDataStatusRef.current = 3;
							setPostQCResponse((preList) => [
								...preList,
								{
									user: `${userHandle}`,
									time: new Date().toLocaleTimeString([], {
										hour: '2-digit',
										minute: '2-digit'
									}),
									date: new Date().toLocaleDateString('en-GB'),
									error: 'error fetching data from index calc',
									failed_at: 'websocket error',
									execution_id: runIndexCalc.data.execution_id
								}
							]);
							reconnectAttempts.current = 5;
							ws.current?.close();
						});
				} else if (data.hasOwnProperty('url') && data.url === null) {
					failPostQCProcess();
					//console.log('no url');
					setError({
						stage: 'websocket (no url in data)',
						error: 'No url in data'
					});
					fetchWSDataStatusRef.current = 2;

					addToastContent(`Post QC data: no data for this site`);

					reconnectAttempts.current = 5;
					ws.current?.close();
				} else if (data.message === 'Process status is FAILED') {
					fetchWSDataStatusRef.current = 3;
					failPostQCProcess();

					addToastContent(
						<>
							Post QC data: error (process failed) <br></br>
							{data?.error && data.error} <br></br>
							{data?.cause && data.cause}
						</>,
						true
					);

					setError({
						stage: 'websocket (fetching Post QC data)',
						error: 'Process status is FAILED'
					});

					websocketStatusRef.current = 2;
					fetchWSDataStatusRef.current = 2;
					postQCStatusRef.current = 3;
					console.log('postQCStatusRef should be 3');

					setPostQCResponse((preList) => [
						...preList,
						{
							user: `${userHandle}`,
							time: new Date().toLocaleTimeString([], {
								hour: '2-digit',
								minute: '2-digit'
							}),
							date: new Date().toLocaleDateString('en-GB'),
							error: data.message,
							failed_at: 'websocket error',
							execution_id: runIndexCalc.data.execution_id
						}
					]);
					reconnectAttempts.current = 5;

					ws.current?.close();
					//	console.log('Process failed:', data.message);
				} else {
					console.log('WS no message no error');
					console.log(data.message);
				}
			};

			ws.current.onerror = (error) => {
				console.error('WebSocket error:', error);
				websocketStatusRef.current = 3;
				failPostQCProcess();

				setError({ stage: 'websocket', error: error });

				addToastContent(
					<>
						<i class="fa-solid fa-plug-circle-exclamation pe-2"></i> WS error{' '}
						{error}
					</>
				);
			};

			ws.current.onclose = (event) => {
				//console.log('WebSocket connection closed', event);

				if (fetchWSDataStatusRef.current === 2) {
					//console.log('should be closing websocket and not reopeining');
					reconnectAttempts.current = 5;
					websocketStatusRef.current = 2;

					addToastContent(
						<>
							<i class="fa-solid fa-plug-circle-xmark pe-2"></i> WS process
							completed
						</>
					);

					if (postQCStatusRef.current !== 3) {
						completePostQCProcess();
						addToastContent(`Ready for upload`);
					}

					console.log(
						'Max reconnect attempts reached or process completed. Not retrying.'
					);
				} else {
					//console.log('here in websocket');

					addToastContent(
						<>
							<i class="fa-solid fa-plug-circle-xmark pe-2"></i> WS timeout
						</>
					);

					if (reconnectAttempts.current < maxReconnectAttempts) {
						websocketStatusRef.current = 1;
						const delay =
							reconnectDelay * Math.pow(2, reconnectAttempts.current);
						//console.log(`Reconnecting in ${delay / 1000} seconds...`);
						//console.log(reconnectAttempts2);

						addToastContent(
							<>
								<i className="fa-solid fa-plug-circle-check pe-2"></i> WS
								reconnecting (attempt {reconnectAttempts2}) ...
							</>
						);

						setTimeout(connectWebSocket, delay);
						reconnectAttempts2++;
						reconnectAttempts.current++;
					}
				}
			};
		};

		connectWebSocket();

		//console.log(reconnectAttempts2);

		return () => {
			console.log('Cleaning up WebSocket connection');

			isUnmounted.current = true;
			ws.current?.close();
		};
	}, [socketUrl, indexCalcStatusRef.current]);

	useEffect(() => {
		if (!postQCData) return;

		if (postQCData.length === 0) {
			if (indexCalcStatusRef.current > 0) {
				addToastContent('Post QC data processed: No Data');
			}

			return;
		}

		let filteredData = postQCData;

		const startDate = new Date(formValue.formStartDate);
		const endDate = formValue.formEndDate
			? new Date(formValue.formEndDate)
			: null;

		filteredData = postQCData.filter((capture) => {
			const capturedDate = new Date(capture.captured);

			return capturedDate >= startDate && (!endDate || capturedDate <= endDate);
		});

		const uniqueSiteIds = [
			...new Set(filteredData.map((item) => item.site_id))
		];

		const capturesGroupedById = uniqueSiteIds.map((siteId) => {
			let siteCaptures = filteredData.filter(
				(capture) => capture.site_id === siteId
			);

			if (!toggleFill) {
				return siteCaptures;
			}

			const unfilledCaptures = siteCaptures.filter(
				(capture) => capture.filled !== 1
			);

			if (unfilledCaptures.length > 0) {
				return unfilledCaptures;
			}

			// keep a placeholder row for sites where every capture has fill = 1
			return [
				Object.fromEntries(
					Object.entries(siteCaptures[0]).map(([key, value]) => [
						key,
						key === 'site_id' ? value : '-'
					])
				)
			];
		});

		setPostQCDataFiltered(filteredData);
		setUniqueSites(uniqueSiteIds.length);
		setTablesData(capturesGroupedById);
		pipelineContextQCUpload.setPostQCData(postQCData);
		completePostQCProcess();
	}, [postQCData, toggleFill]);

	useEffect(() => {
		if (!postQCData?.length) return;

		const uniqueSiteCount = new Set(postQCData.map((item) => item.site_id))
			.size;

		addToastContent(
			`Post QC data processed: Captures (${postQCData.length}), Sites (${uniqueSiteCount})`
		);
	}, [postQCData]);

	useEffect(() => {
		if (
			postQCData &&
			tablesData &&
			(indexCalcStatusRef.current === 2 || formValue.uploadStaticPostQCDataFile)
		) {
			setTotalPages(Math.ceil(tablesData.length / itemsPerPage));

			setPaginatedData(
				tablesData.slice(
					(currentPage - 1) * itemsPerPage,
					currentPage * itemsPerPage
				)
			);
		}
	}, [tablesData, currentPage]);

	const formatDate = (date) => {
		if (!date) return '';

		return new Date(date).toISOString().slice(0, 10);
	};

	const getEarliestDate = (captures) => {
		if (!captures?.length) return null;

		return captures.reduce(
			(earliest, capture) =>
				new Date(capture.captured) < new Date(earliest)
					? capture.captured
					: earliest,
			captures[0].captured
		);
	};

	const getLatestDate = (captures) => {
		if (!captures?.length) return null;

		return captures.reduce(
			(latest, capture) =>
				new Date(capture.captured) > new Date(latest)
					? capture.captured
					: latest,
			captures[0].captured
		);
	};

	const runUploadToDB = async () => {
		if (uploadStatusRef.current === 2 || uploadAttempts >= 2) return;

		startUploadProcess();

		setUploadAttempts((prev) => prev + 1);
		uploadStatusRef.current = 1;

		// addToastContent(
		// 	<ProcessTimerToast
		// 		startedAtRef={uploadStartedAtRef}
		// 		finishedAtRef={uploadFinishedAtRef}
		// 		processStateRef={uploadProcessStateRef}
		// 		runningMessage="Uploading post-QC data"
		// 		completedMessage="Upload completed"
		// 		errorMessage="Upload failed"
		// 	/>
		// );
		addToastContent('Upload complete');

		const pipelineCaptures = [
			...(pipelineContextQCUpload?.capsInPipeline ?? []),
			...(pipelineContextQCUpload?.autoQCCaps ?? [])
		];

		const hasPipelineCaptures = pipelineCaptures.length > 0;
		const hasPostQCData = postQCData?.length > 0;

		let earliestStartDate;
		let latestEndDate;

		if (hasPipelineCaptures) {
			earliestStartDate = getEarliestDate(pipelineCaptures);
			latestEndDate = getLatestDate(pipelineCaptures);
		} else if (formValue.formStartDate) {
			earliestStartDate = formValue.formStartDate;
			latestEndDate = formValue.formEndDate || formValue.formStartDate;
		} else if (hasPostQCData) {
			earliestStartDate = getEarliestDate(postQCData);
			latestEndDate = getLatestDate(postQCData);
		} else {
			earliestStartDate = '2016-01-01';
			latestEndDate = new Date();
		}

		earliestStartDate = formatDate(earliestStartDate);
		latestEndDate = formatDate(latestEndDate);

		let bbody = {
			data: postQCData,
			start_date: earliestStartDate,
			end_date: latestEndDate
		};

		let toastBody = {
			data: `${postQCData.length} captures`,
			start_date: earliestStartDate,
			end_date: latestEndDate
		};

		addToastContent(`Upload body: ${JSON.stringify(toastBody, null, 2)}`);

		console.log('upload body', bbody);

		let uploadBody;

		try {
			const jsonString = JSON.stringify(bbody);
			const compressedData = Pako.gzip(jsonString);

			uploadBody = Buffer.from(compressedData).toString('base64');

			console.log(uploadBody);

			uploadToDB.makeRequest(
				`/${pipelineContextQCUpload.comm}/upload`,
				'POST',
				uploadBody,
				pipelineContextQCUpload.notificationsEnabled
					? 'upload to database'
					: null
			);
		} catch (error) {
			console.error('Error compressing JSON:', error);
			uploadStatusRef.current = 3;

			addToastContent(
				'Upload: error compressing post QC data for upload',
				true
			);

			setUploadResponse((prevList) => [
				...prevList,
				{
					user: `${userHandle}`,
					time: new Date().toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit'
					}),
					date: new Date().toLocaleDateString('en-GB'),
					error: 'Failed to compress post QC data',
					failed_at: 'upload'
				}
			]);
		}
	};

	useEffect(() => {
		if (
			!uploadToDB.error &&
			uploadToDB.data &&
			uploadToDB.data?.message === 'Captures Uploaded!'
		) {
			pipelineContextQCUpload.setUploadPerformed(true);
			uploadStatusRef.current = 2;
			completeUploadProcess();
			addToastContent(`Upload: succesfully uploaded to database`);
		}
		if (uploadToDB.error) {
			setError({ stage: 'Upload to database', error: uploadToDB.error });
			addToastContent('Upload failed', true);
			failUploadProcess();
			addToastContent(
				<>
					Upload: {uploadToDB.error?.message && uploadToDB.error.message} |{' '}
					{uploadToDB.error?.status && uploadToDB.error.status}
				</>,
				true
			);

			uploadStatusRef.current = 3;
			setUploadResponse((prevList) => [
				...prevList,
				{
					user: `${userHandle}`,
					time: new Date().toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit'
					}),
					date: new Date().toLocaleDateString('en-GB'),
					error: uploadToDB.error,
					failed_at: 'upload'
				}
			]);
		}
	}, [uploadToDB.data, uploadToDB.error]);

	const finishQC = async () => {
		if (capsToSendBack.length > 0) {
			await sendCapsBack(
				`/${pipelineContextQCUpload.comm}/remove_from_queue`,
				'POST',
				{
					commodityIds: `${formValue.formCommodities.join(',')}`,
					qcUser: `${userHandle}`,
					captureIds: capsToSendBack.map((cap) => cap.id).join(',')
				},
				pipelineContextQCUpload.notificationsEnabled
					? 'sending non-QCd caps back'
					: null
			);
		}

		setIsToastVisible(false);
		setTimeout(() => {
			pipelineContextQCUpload.setProgressBar({
				width: 100,
				title: 'QC Report'
			});
			pipelineContextQCUpload.setPostQCPerformed(true);
			pipelineContextQCUpload.setQCInProgress(false);
		}, 500);
	};

	const finishQCEarly = async () => {
		if (capsToSendBack.length > 0) {
			await sendCapsBack(
				`/${pipelineContextQCUpload.comm}/remove_from_queue`,
				'POST',
				{
					commodityIds: `${formValue.formCommodities.join(',')}`,
					qcUser: `${userHandle}`,
					captureIds: capsToSendBack.map((cap) => cap.id).join(',')
				},
				pipelineContextQCUpload.notificationsEnabled
					? 'sending non-QCd caps back'
					: null
			);
		}

		// reset everything else here
		setIsToastVisible(false);

		setTimeout(() => {
			pipelineContextQCUpload.resetState();
			pipelineContextQCUpload.setNavigateHome(true);
		}, 1500);
	};

	const handleBackButton = () => {
		pipelineContextQCUpload.setProgressBar({
			width: 3 * (100 / 7),
			title: 'Upload'
		});
	};

	const renderNoPostQCData = () => {
		if (noPostQCData && !error) {
			return (
				<div className="row px-2 mt-4">
					<div className="col-6 offset-3 mt-5">
						<h4 className="text-center">
							There was no data found upon performing Post QC.
							<br />
							<br /> If you think this is wrong you can contact an admin with{' '}
							<strong>execution id : {executionID && executionID}</strong>.
							<br />
							<br /> You can now exit QC.
						</h4>
					</div>
				</div>
			);
		}
	};

	const renderErrorInProcess = () => {
		if (error && indexCalcStatusRef?.current > 0) {
			return (
				<div className="row px-2 mt-4">
					<div className="col-6 offset-3 mt-5">
						<h3 className="text-center">
							There was an error during this process.
						</h3>
						<h5 className="text-center">
							Please copy the information below and contact an admin.
						</h5>
						<h5 className="text-center">
							Once copied, you can Exit QC via the button on the top right.
						</h5>

						<div className="border border-solid p-4 mt-2">
							<h5 className="text-end">
								<i
									onClick={() => copyPostQCLogToClipboard(toastContent)}
									title="Copy log details to clipboard"
									className="fa-regular fa-copy "
								></i>
							</h5>
							<table>
								<tbody>{renderToastTable(toastContent)}</tbody>
							</table>
						</div>
					</div>
				</div>
			);
		}
	};

	const renderButtons = () => {
		return (
			<div className="row my-2">
				<table style={{ width: '70%', marginLeft: '15%' }}>
					<tbody>
						<tr>
							<td className="d-flex justify-content-center align-items-center p-0">
								{' '}
								<label className="label me-3" for="upload-change-lookback">
									Lookback
								</label>
								<input
									id="upload-change-lookback"
									className="form-control my-1 text-center p-0"
									type="number"
									style={{ width: '80px' }}
									onChange={(e) =>
										setFormValue({
											...formValue,
											formLookback: e.target.value
										})
									}
									min={-1}
									max={7}
									value={formValue.formLookback ? formValue.formLookback : 1}
									disabled={postQCStatusRef.current === 2}
								></input>
							</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
						</tr>
						<tr>
							<td className="d-flex justify-content-center align-items-center pt-0">
								<div className="form-check form-switch ms-3 mt-2">
									<label
										className="form-check-label"
										for="flexSwitchCheckDefault2"
										data-tooltip-id="overwrite-post-QC"
									>
										Overwrite existing data
									</label>
									<input
										className="form-check-input"
										type="checkbox"
										id="flexSwitchCheckDefault2"
										onChange={() =>
											setOverwriteOnPostQC((prevState) => !prevState)
										}
										disabled={postQCStatusRef.current === 2}
									></input>
								</div>
							</td>
							<td></td>
							<td></td>
							<td></td>
							<td></td>
						</tr>

						<tr>
							<td className="text-center">
								{renderPostQCButton(
									indexCalcStatusRef,
									websocketStatusRef,
									postQCStatusRef,
									runPostQCIndexCalc,
									postQCData
								)}
							</td>
							<td className="text-center">
								{indexCalcStatusRef.current === 1 ||
								websocketStatusRef.current === 1 ||
								postQCStatusRef.current === 1 ? (
									<ClipLoader size={18} />
								) : (
									<i class="fa-solid fa-circle-arrow-right"></i>
								)}
							</td>
							<td className="text-center">
								{renderUploadButton(
									postQCStatusRef,
									uploadStatusRef,
									runUploadToDB,
									noPostQCData
								)}
							</td>
							<td className="text-center">
								{uploadStatusRef.current === 1 ? (
									<ClipLoader size={18} />
								) : (
									<i class="fa-solid fa-circle-arrow-right"></i>
								)}
							</td>
							<td className="text-center">
								{renderFinishQCButton(
									postQCStatusRef,
									finishQCEarly,
									finishQC,
									noPostQCData,
									postQCResponse,
									uploadStatusRef,
									uploadResponse
								)}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	};

	const renderAdditionalTooltips = () => {
		return (
			<>
				<Tooltip
					id="overwrite-post-QC"
					place="bottom"
					effect="solid"
					style={{ zIndex: '9999' }}
				>
					If overwrite is true, the index value on captures already QC'd will be
					updated and overwritten
				</Tooltip>

				<Tooltip
					id="indexCalcErrorTT"
					place="bottom"
					effect="solid"
					style={{ zIndex: '9999' }}
				>
					<i className="fa-solid fa-triangle-exclamation pe-2"></i> There has
					been an error performing the Index Calculation.
					<br />
					Attempts: {postQCResponse.length} <br />
					{postQCResponse.length < 2 && (
						<>Please try again, or Exit QC and try again later.</>
					)}
					<br />
					{postQCResponse.length >= 2 && (
						<>Please Exit QC and try again later.</>
					)}
					<br />
					You can show this error to the admin.
					<br /> <br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty(
							'user'
						) && (
							<>User: {postQCResponse[postQCResponse.length - 1]['user']}</>
						)}{' '}
					<br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty(
							'time'
						) && (
							<>Time: {postQCResponse[postQCResponse.length - 1]['time']}</>
						)}{' '}
					<br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty(
							'date'
						) && (
							<>Date: {postQCResponse[postQCResponse.length - 1]['date']}</>
						)}{' '}
					<br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty(
							'failed_at'
						) && (
							<>
								Failed at:{' '}
								{postQCResponse[postQCResponse.length - 1]['failed_at']}
							</>
						)}{' '}
					<br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty('error') &&
						postQCResponse[postQCResponse.length - 1].error.hasOwnProperty(
							'message'
						) && (
							<>
								Error:{' '}
								{postQCResponse[postQCResponse.length - 1]['error']['message']}{' '}
								<br />
								Status:{' '}
								{postQCResponse[postQCResponse.length - 1]['error']['status']}
							</>
						)}{' '}
					<br />
					{postQCResponse.length > 0 &&
						postQCResponse[postQCResponse.length - 1].hasOwnProperty(
							'execution_id'
						) && (
							<>
								Execution ID:{' '}
								{postQCResponse[postQCResponse.length - 1]['execution_id']}
							</>
						)}{' '}
					<br />
				</Tooltip>

				<Tooltip
					id="uploadErrorTT"
					place="bottom"
					effect="solid"
					style={{ zIndex: '9999' }}
				>
					<i className="fa-solid fa-triangle-exclamation pe-2"></i> There has
					been an error performing the Index Calculation.
					<br />
					Attempts: {uploadResponse.length} <br />
					{uploadResponse.length < 2 && (
						<>Please try again, or Exit QC and try again later.</>
					)}
					<br />
					{uploadResponse.length >= 2 && (
						<>Please Exit QC and try again later.</>
					)}
					<br />
					You can show this error to the admin.
					<br /> <br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'user'
						) && (
							<>User: {uploadResponse[uploadResponse.length - 1]['user']}</>
						)}{' '}
					<br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'time'
						) && (
							<>Time: {uploadResponse[uploadResponse.length - 1]['time']}</>
						)}{' '}
					<br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'date'
						) && (
							<>Date: {uploadResponse[uploadResponse.length - 1]['date']}</>
						)}{' '}
					<br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'failed_at'
						) && (
							<>
								Failed at:{' '}
								{uploadResponse[uploadResponse.length - 1]['failed_at']}
							</>
						)}{' '}
					<br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'error'
						) && (
							<>
								Error:{' '}
								{uploadResponse[uploadResponse.length - 1]['error']['message']}{' '}
								<br />
								Status:{' '}
								{uploadResponse[uploadResponse.length - 1]['error']['status']}
							</>
						)}{' '}
					<br />
					{uploadResponse.length > 0 &&
						uploadResponse[uploadResponse.length - 1].hasOwnProperty(
							'execution_id'
						) && (
							<>
								Execution ID:{' '}
								{uploadResponse[uploadResponse.length - 1]['execution_id']}
							</>
						)}{' '}
					<br />
				</Tooltip>
			</>
		);
	};

	const renderTitleRow = () => {
		return (
			<div className="row mb-2">
				<hr></hr>

				{/* BACK BUTTON */}
				{pipelineContextQCUpload.straightToUpload && (
					<div className="row">
						<div className="col d-flex justify-content-start align-items-center">
							<button
								className="btn interactButtonSecondary"
								onClick={handleBackButton}
								data-tooltip-id={
									postQCStatusRef.current === 2
										? 'backButtonDisabledTT'
										: 'backButtonNotDisabledTT'
								}
								style={{ width: '200px' }}
								disabled={postQCStatusRef.current === 2}
							>
								<i className="fa-solid fa-left-long pe-3"></i>
								Back
							</button>
						</div>

						<Tooltip id="backButtonDisabledTT" place="bottom" effect="solid">
							The index calculation has been performed for the chosen criteria,
							therefore you cannot go back to the form
						</Tooltip>

						<Tooltip id="backButtonNotDisabledTT" place="bottom" effect="solid">
							Go back to the form and choose different upload criteria
						</Tooltip>

						<div className="col d-flex justify-content-end  align-items-center"></div>
					</div>
				)}

				{/* TITLE / DESCRIPT */}
				<div className="row mt-4">
					<div className="col-9">
						<h2>Upload Captures </h2>

						<h5>
							<i
								data-tooltip-id="uploadPageViewNotifications"
								onClick={() => toggleToast(setIsToastVisible)}
								disabled={toastContent.length === 0}
								className={
									isToastVisible
										? 'fa-solid fa-clipboard-list pe-2'
										: 'fa-solid fa-clipboard pe-2'
								}
							></i>
							{pipelineContextQCUpload.straightToUpload ? (
								<>Perform upload for the given criteria </>
							) : (
								<>Use this page to upload QC'd captures to the database</>
							)}
						</h5>

						<Tooltip
							id="uploadPageViewNotifications"
							place="bottom"
							effect="solid"
						>
							{isToastVisible ? <>Hide info log</> : <>View info log</>}
						</Tooltip>
					</div>
				</div>
			</div>
		);
	};

	const renderUploadSummaryInfoTable = () => {
		if (formValue.manuallySelectSites) {
			return (
				<div className="col-4 offset-4">
					<div
						className="table-responsive"
						style={{
							maxWidth: '100%',
							maxHeight: '500px'
						}}
					>
						<table className="table">
							<tbody className="table-light">
								<tr>
									<td>Site ID (s)</td>
									<td>
										<ul>
											{formValue.formSiteId.map((item) => (
												<li>{item}</li>
											))}
										</ul>
									</td>
								</tr>
								<tr>
									<td>Start Date</td>
									<td>{formValue.formStartDate}</td>
								</tr>
								<tr>
									<td>End Date</td>
									<td>{formValue.formEndDate}</td>
								</tr>

								<tr>
									<td>Overwrite existing data</td>
									<td>{overwriteOnPostQC ? <>yes</> : <>no</>}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			);
		} else {
			return (
				<div className="col-4 offset-4">
					<div
						className="table-responsive"
						style={{
							maxWidth: '100%',
							maxHeight: '500px'
						}}
					>
						<table className="table">
							<tbody className="table-light">
								<tr>
									<td>Commodity (s)</td>
									<td>
										<ul>
											{formValue.formCommodities.map((item) => (
												<li>
													{
														endpointContextQCUpload.dbData.allCommodities.filter(
															(comm) => comm.id === item
														)[0].id
													}
												</li>
											))}
										</ul>
									</td>
								</tr>
								<tr>
									<td>Production State (s)</td>
									<td>
										<ul>
											{formValue.formProdStates.map((item, key) => (
												<>
													<li data-tooltip-id={`key${key}`}>{item}</li>
													<Tooltip
														id={`key${key}`}
														place="bottom"
														effect="solid"
													>
														{
															exportConfig.production_states.filter(
																(prod_state) => prod_state.state === item
															)[0].descript
														}
													</Tooltip>
												</>
											))}
										</ul>
									</td>
								</tr>

								<tr>
									<td>Lookback</td>
									<td>{formValue.formLookback}</td>
								</tr>

								<tr>
									<td>Start Date</td>
									<td>{formValue.formStartDate}</td>
								</tr>

								<tr>
									<td>End Date</td>
									<td>{formValue.formEndDate}</td>
								</tr>

								<tr>
									<td>Overwrite existing data</td>
									<td>{overwriteOnPostQC ? <>yes</> : <>no</>}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			);
		}
	};

	const renderCapturesTables = () => {
		return (
			<>
				<div className="my-2">
					<h5>
						Manually QC'd captures ({manCapsUpload.length})
						<span>
							<i
								className={
									manCapsCollapsed
										? 'fa-solid fa-caret-up ms-2'
										: 'fa-solid fa-caret-down ms-2'
								}
								onClick={() => setManCapsCollapsed(!manCapsCollapsed)}
								href="#manualCapturesUploadTableCollapse"
								data-bs-target="#manCapsCollapsed"
								role="button"
								aria-expanded="false"
								aria-controls="manCapsCollapsed"
							></i>
						</span>
					</h5>

					<div
						className={`collapse ${manCapsCollapsed ? 'show' : ''}`}
						id="manualCapturesUploadTableCollapse"
					>
						<CapturesTable
							fields={smelterFieldsUploadManualQC}
							captures={manCapsUpload}
							qcCat="smelters"
							tableHeight="400px"
						/>
					</div>
				</div>

				<>
					{pipelineContextQCUpload.qcType !== 'Re-QC' && (
						<div className="my-2">
							<h5>
								Auto QC'd captures ({pipelineContextQCUpload.autoQCCaps.length})
								<span>
									<i
										className={
											autoCapsCollapsed
												? 'fa-solid fa-caret-up ms-2'
												: 'fa-solid fa-caret-down ms-2'
										}
										onClick={() => setAutoCapsCollapsed(!autoCapsCollapsed)}
										href="#autoCapturesUploadTableCollapse"
										data-bs-target="#autoCapsCollapsed"
										role="button"
										aria-expanded="false"
										aria-controls="autoCapsCollapsed"
									></i>
								</span>
							</h5>

							<div
								className={`collapse ${autoCapsCollapsed ? 'show' : ''}`}
								id="autoCapturesUploadTableCollapse"
							>
								<CapturesTable
									fields={smelterFieldsUploadAutoQC}
									captures={pipelineContextQCUpload.autoQCCaps}
									qcCat="smelters"
									tableHeight="400px"
								/>
							</div>
						</div>
					)}
				</>
			</>
		);
	};

	console.log('postQCDataFiltered', postQCDataFiltered);
	console.log(postQCProcessState, postQCElapsedSeconds);

	return (
		<>
			<div style={{ padding: '10px' }}>
				{renderTitleRow()}

				{/* {postQCProcessState === 'running' && (
					<div className="d-flex align-items-center gap-2 text-muted mt-2">
						<div className="spinner-border spinner-border-sm" role="status" />
						<span>
							Processing post-QC data… {formatElapsedTime(postQCElapsedSeconds)}
						</span>
					</div>			
				)} */}

				{renderButtons()}

				{renderNoPostQCData()}

				{renderErrorInProcess()}

				{/* TABLES */}
				{!pipelineContextQCUpload.straightToUpload ? (
					<>
						<div className="row px-2">
							<div className="row mb-3">
								<div className="col d-flex align-items-center">
									{postQCStatusRef.current === 2 &&
										renderToggleFill(setToggleFill, noPostQCData)}
								</div>
								<div className="col d-flex align-items-center justify-content-center">
									{postQCStatusRef.current === 2 &&
										renderPagination(
											uniqueSites,
											itemsPerPage,
											currentPage,
											totalPages,
											setCurrentPage,
											getPageNumbers
										)}
								</div>
								<div className="col d-flex justify-content-end  align-items-center">
									{!noPostQCData || postQCStatusRef.current === 0 ? (
										<>
											Total captures: {manCapsUpload.length} / sites :{' '}
											{uniqueSites}
										</>
									) : (
										postQCStatusRef.current === 2 && (
											<>
												Total captures:{' '}
												{postQCDataFiltered && postQCDataFiltered.length} /
												sites : {uniqueSites}
											</>
										)
									)}
								</div>
							</div>

							{!runIndexCalc.loading && !postQCData && !socketUrl && (
								<>{renderCapturesTables()}</>
							)}

							<PostQCDataTable
								tablesData={paginatedData}
								loading={postQCDataLoading}
								error={postQCResponse.length > 2}
							/>
						</div>
					</>
				) : (
					<>
						<div className="row px-2 mt-5">
							{postQCStatusRef.current === 2 && (
								<div className="row mb-3">
									<div className="col d-flex align-items-center">
										{renderToggleFill(setToggleFill, noPostQCData)}
									</div>

									<div className="col d-flex align-items-center justify-content-center">
										{renderPagination(
											uniqueSites,
											itemsPerPage,
											currentPage,
											totalPages,
											setCurrentPage,
											getPageNumbers
										)}
									</div>

									<div className="col d-flex align-items-center justify-content-end">
										Total captures:{' '}
										{postQCDataFiltered && postQCDataFiltered.length} / sites :{' '}
										{uniqueSites}
									</div>
								</div>
							)}

							<div>
								{!runIndexCalc.loading && !postQCData && !socketUrl && (
									<>{renderUploadSummaryInfoTable()}</>
								)}

								<PostQCDataTable
									tablesData={paginatedData}
									loading={postQCDataLoading}
									error={postQCResponse.length > 2}
								/>
							</div>
						</div>
					</>
				)}

				{renderAdditionalTooltips()}
			</div>
		</>
	);
};

export default QCUpload;
